import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, canWrite } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Printer, ClipboardList } from "lucide-react";

// ---------------------------------------------------------------------------
// MP12 Module 2B — Have / Have Not print-ready checklist.
//
// Rendered as a clean, printable page derived from the workspace record via
// GET /api/intake-workspaces/{id}/checklist/export. Includes a Print button
// that triggers the browser's native print dialog; the on-screen chrome is
// hidden in print CSS so the printed page is minimal.
// ---------------------------------------------------------------------------

const STATUS_STYLES = {
  HAVE:           { bg: "hsl(150 40% 92%)", fg: "hsl(150 45% 25%)", border: "hsl(150 40% 70%)" },
  NOT_PROVIDED:   { bg: "hsl(220 8% 94%)",  fg: "hsl(220 6% 40%)",  border: "hsl(220 6% 78%)" },
  NOT_APPLICABLE: { bg: "hsl(210 10% 94%)", fg: "hsl(210 8% 42%)",  border: "hsl(210 8% 78%)" },
  NEEDS_REVIEW:   { bg: "hsl(40 55% 92%)",  fg: "hsl(35 60% 30%)",  border: "hsl(40 55% 74%)" },
  DEFICIENT:      { bg: "hsl(0 60% 94%)",   fg: "hsl(0 65% 32%)",   border: "hsl(0 55% 74%)" },
  VERIFIED:       { bg: "hsl(190 60% 92%)", fg: "hsl(190 60% 28%)", border: "hsl(190 55% 72%)" },
};

function StatusChip({ value }) {
  const st = STATUS_STYLES[value] || STATUS_STYLES.NOT_PROVIDED;
  return (
    <span
      className="text-[10px] font-semibold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded"
      style={{ background: st.bg, color: st.fg, border: `1px solid ${st.border}` }}
    >
      {value}
    </span>
  );
}

export default function IntakeChecklistPrint() {
  const { id } = useParams();
  const { user } = useAuth();
  const writer = canWrite(user);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/intake-workspaces/${id}/checklist/export`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load checklist.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!writer) {
    return (
      <div className="p-6 text-[13px] text-muted-foreground" data-testid="checklist-print-readonly">
        Internal Intake checklist export is visible to Catalog Manager, GFMP Reviewer, and Super Admin only.
      </div>
    );
  }

  if (loading || !data) {
    return <div className="p-6 text-[13px] text-muted-foreground">Loading checklist…</div>;
  }

  return (
    <div className="p-6 max-w-[860px] mx-auto space-y-4" data-testid="checklist-print">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .checklist-print-doc { box-shadow: none !important; }
        }
        .checklist-print-doc {
          background: white; color: hsl(220 10% 12%);
          padding: 32px; border-radius: 8px;
          box-shadow: 0 1px 3px hsl(220 20% 12% / 0.08);
        }
      `}</style>

      <div className="flex items-center gap-2 no-print">
        <Button asChild variant="ghost" size="sm" data-testid="checklist-print-back">
          <Link to={`/intake-workspaces/${id}`}>
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to intake
          </Link>
        </Button>
        <div className="ml-auto flex gap-2">
          <Button size="sm" onClick={() => window.print()} data-testid="checklist-print-btn" style={{ background: "hsl(347 72% 22%)" }}>
            <Printer className="w-3.5 h-3.5 mr-1.5" /> Print / Export as PDF
          </Button>
        </div>
      </div>

      <div className="checklist-print-doc">
        <div className="flex items-center gap-2 mb-4">
          <ClipboardList className="w-4 h-4" style={{ color: "hsl(347 60% 22%)" }} />
          <h1 className="font-display text-[22px]">Internal Intake — Have / Have Not</h1>
          <Badge
            variant="outline"
            className="ml-2 text-[10px] uppercase tracking-[0.14em] font-semibold"
            style={{ background: "hsl(40 55% 94%)", color: "hsl(35 60% 30%)", borderColor: "hsl(40 55% 78%)" }}
          >
            Provisional
          </Badge>
        </div>

        <div className="grid md:grid-cols-2 gap-2 text-[12.5px] mb-4">
          <div><span className="uppercase tracking-wide text-[10px] text-muted-foreground">Client</span><div>{data.client_name || "—"}</div></div>
          <div><span className="uppercase tracking-wide text-[10px] text-muted-foreground">Entity</span><div>{data.entity_name || "—"}</div></div>
          <div><span className="uppercase tracking-wide text-[10px] text-muted-foreground">Asset title</span><div>{data.asset_title || "—"}</div></div>
          <div><span className="uppercase tracking-wide text-[10px] text-muted-foreground">Artist</span><div>{data.asset_artist || "—"}</div></div>
          <div><span className="uppercase tracking-wide text-[10px] text-muted-foreground">Workspace status</span><div>{data.status || "—"}</div></div>
          <div><span className="uppercase tracking-wide text-[10px] text-muted-foreground">Generated</span><div className="font-mono text-[11px]">{data.generated_at}</div></div>
          <div><span className="uppercase tracking-wide text-[10px] text-muted-foreground">Generated by</span><div>{data.generated_by || "—"}</div></div>
          <div><span className="uppercase tracking-wide text-[10px] text-muted-foreground">Workspace id</span><div className="font-mono text-[11px]">{data.workspace_id}</div></div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4 text-[10.5px]">
          {Object.entries(data.counts || {}).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1">
              <StatusChip value={k} /> <span className="font-mono">{v}</span>
            </span>
          ))}
        </div>

        <table className="w-full text-[12.5px] border-collapse">
          <thead>
            <tr className="border-b">
              <th className="text-left py-1.5 pr-2 uppercase tracking-wide text-[10px] text-muted-foreground w-[42%]">Document</th>
              <th className="text-left py-1.5 pr-2 uppercase tracking-wide text-[10px] text-muted-foreground w-[18%]">Status</th>
              <th className="text-left py-1.5 uppercase tracking-wide text-[10px] text-muted-foreground">Notes</th>
            </tr>
          </thead>
          <tbody>
            {(data.items || []).map((it) => (
              <tr key={it.category + ":" + (it.label || "")} className="border-b align-top" data-testid={`checklist-print-row-${it.category}`}>
                <td className="py-1.5 pr-2">
                  <div className="font-medium">{it.label || it.category}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{it.category}</div>
                </td>
                <td className="py-1.5 pr-2"><StatusChip value={it.status} /></td>
                <td className="py-1.5 whitespace-pre-wrap">{it.notes || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="text-[10.5px] text-muted-foreground mt-6">
          This document is derived from a provisional intake workspace and does not represent a verified rights, work, master, or compliance record.
        </div>
      </div>
    </div>
  );
}
